import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, ArrowLeft, Loader2, Camera, User } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProfessionalRegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [options, setOptions] = useState({
    professions: [],
    nukhs: [],
    states: [],
    qualifications: [],
    countries: [],
    statesByCountry: {},
    citiesByCountry: {},
    citiesByState: {}
  });

  const [formData, setFormData] = useState({
    // Personal Details
    fullName: '',
    fatherName: '',
    motherName: '',
    nukh: '',
    dateOfBirth: '',
    // Contact Details
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    whatsapp: '',
    // Current Location
    currentCountry: 'India',
    currentState: '',
    currentCity: '',
    currentPlace: '',
    currentPincode: '',
    // Origin Location
    originCountry: 'India',
    originState: '',
    originCity: '',
    originPlace: '',
    // Professional Details
    profession: '',
    professionOther: '',
    specialization: '',
    qualification: '',
    experienceYears: '',
    organization: '',
    designation: '',
    officeAddress: '',
    // Profile
    bio: '',
    website: '',
    additionalInfo: '',
    linkedIn: '',
    // Conclave Interest
    interestedInConclave2026: false
  });

  // Check if country is "Other" (requires manual text input)
  const isOtherCountry = (country) => country === 'Other';

  // Get states based on selected country
  const getStatesForCountry = (country) => {
    if (!country || !options.statesByCountry || isOtherCountry(country)) return [];
    return options.statesByCountry[country] || [];
  };

  // Get cities based on state (for India) or country (for others)
  const getCitiesForState = (country, state) => {
    if (!country || isOtherCountry(country)) return [];
    
    // For India, use state-specific cities
    if (country === 'India' && state && options.citiesByState) {
      return options.citiesByState[state] || [];
    }
    
    // For other countries, use country-level cities
    if (options.citiesByCountry) {
      return options.citiesByCountry[country] || [];
    }
    
    return [];
  };

  // Refresh local city options immediately after a custom city is saved.
  const handleCustomCitySaved = (cityName, ctry, st) => {
    setOptions(prev => {
      if (ctry === 'India' && st) {
        const list = prev.citiesByState?.[st] || [];
        if (list.includes(cityName)) return prev;
        return { ...prev, citiesByState: { ...prev.citiesByState, [st]: [...list, cityName].sort((a, b) => a.localeCompare(b)) } };
      }
      if (ctry) {
        const list = prev.citiesByCountry?.[ctry] || [];
        if (list.includes(cityName)) return prev;
        return { ...prev, citiesByCountry: { ...prev.citiesByCountry, [ctry]: [...list, cityName].sort((a, b) => a.localeCompare(b)) } };
      }
      return prev;
    });
  };


  useEffect(() => {
    fetchOptions();
  }, []);

  // Reset current state and city when current country changes
  useEffect(() => {
    if (formData.currentCountry) {
      setFormData(prev => ({
        ...prev,
        currentState: '',
        currentCity: ''
      }));
    }
  }, [formData.currentCountry]);

  // Reset current city when current state changes
  useEffect(() => {
    if (formData.currentState) {
      setFormData(prev => ({
        ...prev,
        currentCity: ''
      }));
    }
  }, [formData.currentState]);

  // Reset origin state and city when origin country changes
  useEffect(() => {
    if (formData.originCountry) {
      setFormData(prev => ({
        ...prev,
        originState: '',
        originCity: ''
      }));
    }
  }, [formData.originCountry]);

  // Reset origin city when origin state changes
  useEffect(() => {
    if (formData.originState) {
      setFormData(prev => ({
        ...prev,
        originCity: ''
      }));
    }
  }, [formData.originState]);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, PNG, and WebP images are allowed');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Required fields validation
    const requiredFields = [
      { field: 'fullName', label: 'Full Name' },
      { field: 'fatherName', label: "Father's Name" },
      { field: 'nukh', label: 'Nukh' },
      { field: 'dateOfBirth', label: 'Date of Birth' },
      { field: 'currentPlace', label: 'Area / Locality' },
      { field: 'currentPincode', label: 'Pincode' },
      { field: 'currentCity', label: 'City' },
      { field: 'originCity', label: 'City (Origin)' },
      { field: 'originPlace', label: 'Village / Area (Origin)' },
      { field: 'specialization', label: 'Specialization' },
      { field: 'qualification', label: 'Qualification' },
      { field: 'experienceYears', label: 'Experience' },
      { field: 'organization', label: 'Organization / Company' },
      { field: 'designation', label: 'Designation' },
      { field: 'officeAddress', label: 'Office Address' },
      { field: 'bio', label: 'About / Bio' }
    ];
    
    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        toast.error(`${label} is required`);
        return;
      }
    }
    
    // Password validation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!formData.currentCountry || !formData.currentState || !formData.currentCity) {
      toast.error('Please fill in all current location fields');
      return;
    }

    setLoading(true);

    try {
      // Transform form data to backend format
      const submitData = {
        fullName: formData.fullName,
        fatherName: formData.fatherName,
        motherName: formData.motherName,
        nukh: formData.nukh,
        dateOfBirth: formData.dateOfBirth,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        whatsapp: formData.whatsapp,
        // Current location becomes the main location
        country: formData.currentCountry,
        state: formData.currentState,
        city: formData.currentCity,
        currentPlace: formData.currentPlace,
        pincode: formData.currentPincode,
        // Origin location - individual fields for admin review
        nativeVillage: formData.originPlace || '',
        nativeCity: formData.originCity || '',
        nativeState: formData.originState || '',
        nativeCountry: formData.originCountry || '',
        // Combined native place for display
        nativePlace: formData.originPlace ? 
          `${formData.originPlace}${formData.originCity ? ', ' + formData.originCity : ''}${formData.originState ? ', ' + formData.originState : ''}${formData.originCountry ? ', ' + formData.originCountry : ''}` 
          : '',
        // Professional details
        profession: formData.profession,
        professionOther: formData.professionOther,
        specialization: formData.specialization,
        qualification: formData.qualification,
        experienceYears: formData.experienceYears ? parseInt(formData.experienceYears) : null,
        organization: formData.organization,
        designation: formData.designation,
        officeAddress: formData.officeAddress,
        // Profile
        bio: formData.bio,
        website: formData.website,
        linkedIn: formData.linkedIn,
        interestedInConclave2026: formData.interestedInConclave2026,
        additionalInfo: formData.additionalInfo.trim()
      };

      // First register the professional
      const response = await fetch(`${BACKEND_URL}/api/professionals/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // If there's a profile photo, upload it separately
        if (profilePhoto && data.id) {
          try {
            // Login temporarily to upload photo
            const loginResponse = await fetch(`${BACKEND_URL}/api/professionals/login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: formData.email, password: formData.password })
            });
            
            // Note: Login might fail if admin approval is required
            // In that case, we'll upload the photo after approval
            if (loginResponse.ok) {
              const loginData = await loginResponse.json();
              const photoFormData = new FormData();
              photoFormData.append('photo', profilePhoto);
              
              await fetch(`${BACKEND_URL}/api/professionals/me/photo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${loginData.token}` },
                body: photoFormData
              });
            }
          } catch (photoError) {
            console.log('Photo upload will be available after admin approval');
          }
        }
        
        setSubmitted(true);
        toast.success('Registration submitted successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-12 shadow-floating">
            <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
            <h1 className="font-serif font-bold text-3xl text-primary mb-4">
              Registration Submitted!
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Thank you for registering with the Brahmkshatriya Professional Network. Your registration is pending admin approval.
            </p>
            <div className="bg-muted p-6 rounded-lg mb-6 text-left">
              <p className="font-medium mb-3">What's Next?</p>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Your profile will be reviewed by our admin team</li>
                <li>• Once approved, you'll be able to login</li>
                <li>• You'll appear in the Professional Directory</li>
                <li>• You can browse and connect with other professionals</li>
              </ul>
            </div>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90">
                Back to Home
              </Button>
              <Button onClick={() => navigate('/professionals')} variant="outline">
                Professional Directory
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8 md:p-12 shadow-floating">
          {/* Header */}
          <div className="text-center mb-10 border-b-2 border-secondary pb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-primary mb-3">
              Professional Registration
            </h1>
            <p className="text-muted-foreground">
              व्यावसायिक पंजीकरण - Join the Brahmkshatriya Professional Network
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Personal Information / व्यक्तिगत जानकारी
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name / पूरा नाम <span className="text-destructive">*</span></Label>
                  <Input name="fullName" value={formData.fullName} onChange={handleChange} required />
                </div>
                <div>
                  <Label>Nukh / नुख <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    options={options.nukhs || []}
                    value={formData.nukh}
                    onChange={(value) => setFormData(prev => ({ ...prev, nukh: value }))}
                    placeholder="Type to search nukh..."
                    filterMode="startsWith"
                    allowCustom={false}
                    name="nukh"
                  />
                </div>
                <div>
                  <Label>Father's Name / पिता का नाम <span className="text-destructive">*</span></Label>
                  <Input name="fatherName" value={formData.fatherName} onChange={handleChange} required />
                </div>
                <div>
                  <Label>Mother's / Husband's Name / माता / पति का नाम</Label>
                  <Input name="motherName" value={formData.motherName} onChange={handleChange} />
                </div>
                <div>
                  <Label>Date of Birth / जन्म तिथि <span className="text-destructive">*</span></Label>
                  <Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Contact Information / संपर्क जानकारी
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email <span className="text-destructive">*</span></Label>
                  <Input type="email" name="email" value={formData.email} onChange={handleChange} required />
                </div>
                <div>
                  <Label>Mobile Number <span className="text-destructive">*</span></Label>
                  <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                </div>
                <div>
                  <Label>Password <span className="text-destructive">*</span></Label>
                  <Input type="password" name="password" value={formData.password} onChange={handleChange} required minLength={6} />
                </div>
                <div>
                  <Label>Confirm Password <span className="text-destructive">*</span></Label>
                  <Input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
                </div>
                <div>
                  <Label>WhatsApp Number</Label>
                  <Input type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="Same as mobile if blank" />
                </div>
              </div>
            </div>

            {/* Current Location */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Current Place / वर्तमान निवास
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Country / देश <span className="text-destructive">*</span></Label>
                  <select
                    name="currentCountry"
                    value={formData.currentCountry}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Country</option>
                    {options.countries?.map((country, idx) => (
                      <option key={idx} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>State / Province <span className="text-destructive">*</span></Label>
                  {isOtherCountry(formData.currentCountry) ? (
                    <Input
                      name="currentState"
                      value={formData.currentState}
                      onChange={handleChange}
                      placeholder="Enter state/province name"
                      required
                    />
                  ) : (
                    <select
                      name="currentState"
                      value={formData.currentState}
                      onChange={handleChange}
                      required
                      disabled={!formData.currentCountry}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                    >
                      <option value="">Select State</option>
                      {getStatesForCountry(formData.currentCountry).map((state, idx) => (
                        <option key={idx} value={state}>{state}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <Label>City <span className="text-destructive">*</span></Label>
                  {isOtherCountry(formData.currentCountry) ? (
                    <Input
                      name="currentCity"
                      value={formData.currentCity}
                      onChange={handleChange}
                      placeholder="Enter city name"
                      required
                    />
                  ) : (
                    <SearchableSelect
                      options={getCitiesForState(formData.currentCountry, formData.currentState)}
                      value={formData.currentCity}
                      onChange={(value) => setFormData(prev => ({ ...prev, currentCity: value }))}
                      placeholder="Search or select city..."
                      allowCustom={true}
                      disabled={!formData.currentState}
                      name="currentCity"
                      required
                      saveCustomCity={true}
                      country={formData.currentCountry}
                      state={formData.currentState}
                      onCustomCitySaved={handleCustomCitySaved}
                    />
                  )}
                </div>
                <div>
                  <Label>Area / Locality <span className="text-destructive">*</span></Label>
                  <Input name="currentPlace" value={formData.currentPlace} onChange={handleChange} placeholder="Enter area or locality" required />
                </div>
                <div>
                  <Label>Pincode <span className="text-destructive">*</span></Label>
                  <Input name="currentPincode" value={formData.currentPincode} onChange={handleChange} placeholder="Enter pincode" required />
                </div>
              </div>
            </div>

            {/* Origin Location */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-secondary border-b pb-2">
                Native Place / मूल निवास
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Country / देश</Label>
                  <select
                    name="originCountry"
                    value={formData.originCountry}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Country</option>
                    {options.countries?.map((country, idx) => (
                      <option key={idx} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>State / Province</Label>
                  {isOtherCountry(formData.originCountry) ? (
                    <Input
                      name="originState"
                      value={formData.originState}
                      onChange={handleChange}
                      placeholder="Enter state/province name"
                    />
                  ) : (
                    <select
                      name="originState"
                      value={formData.originState}
                      onChange={handleChange}
                      disabled={!formData.originCountry}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                    >
                      <option value="">Select State</option>
                      {getStatesForCountry(formData.originCountry).map((state, idx) => (
                        <option key={idx} value={state}>{state}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <Label>City <span className="text-destructive">*</span></Label>
                  {isOtherCountry(formData.originCountry) ? (
                    <Input
                      name="originCity"
                      value={formData.originCity}
                      onChange={handleChange}
                      placeholder="Enter city name"
                      required
                    />
                  ) : (
                    <SearchableSelect
                      options={getCitiesForState(formData.originCountry, formData.originState)}
                      value={formData.originCity}
                      onChange={(value) => setFormData(prev => ({ ...prev, originCity: value }))}
                      placeholder="Search or select city..."
                      allowCustom={true}
                      disabled={!formData.originState}
                      name="originCity"
                      required
                      saveCustomCity={true}
                      country={formData.originCountry}
                      state={formData.originState}
                      onCustomCitySaved={handleCustomCitySaved}
                    />
                  )}
                </div>
                <div>
                  <Label>Village / Area <span className="text-destructive">*</span></Label>
                  <Input name="originPlace" value={formData.originPlace} onChange={handleChange} placeholder="Enter village or area name" required />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Professional Information / व्यावसायिक जानकारी
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Profession / पेशा <span className="text-destructive">*</span></Label>
                  <select
                    name="profession"
                    value={formData.profession}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Profession</option>
                    {options.professions.map((prof, idx) => (
                      <option key={idx} value={prof}>{prof}</option>
                    ))}
                  </select>
                </div>
                {formData.profession === 'Other / अन्य' && (
                  <div>
                    <Label>Specify Profession <span className="text-destructive">*</span></Label>
                    <Input name="professionOther" value={formData.professionOther} onChange={handleChange} required />
                  </div>
                )}
                <div>
                  <Label>Specialization / विशेषज्ञता <span className="text-destructive">*</span></Label>
                  <Input name="specialization" value={formData.specialization} onChange={handleChange} placeholder="e.g., Cardiologist, Tax Law, etc." required />
                </div>
                <div>
                  <Label>Qualification / योग्यता <span className="text-destructive">*</span></Label>
                  <Input name="qualification" value={formData.qualification} onChange={handleChange} placeholder="e.g., MBBS, LLB, CA, B.Tech" required />
                </div>
                <div>
                  <Label>Experience (Years) <span className="text-destructive">*</span></Label>
                  <Input type="number" name="experienceYears" value={formData.experienceYears} onChange={handleChange} min="0" required />
                </div>
                <div>
                  <Label>Organization / Company <span className="text-destructive">*</span></Label>
                  <Input name="organization" value={formData.organization} onChange={handleChange} required />
                </div>
                <div>
                  <Label>Designation / पदनाम <span className="text-destructive">*</span></Label>
                  <Input name="designation" value={formData.designation} onChange={handleChange} required />
                </div>
                <div className="md:col-span-2">
                  <Label>Office Address <span className="text-destructive">*</span></Label>
                  <Textarea name="officeAddress" value={formData.officeAddress} onChange={handleChange} rows={2} required />
                </div>
              </div>
            </div>

            {/* Profile & Social */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Profile & Social / प्रोफाइल
              </h3>
              
              {/* Profile Photo Upload */}
              <div className="flex items-start gap-6 p-4 bg-gray-50 rounded-lg">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 shadow-md">
                    <Camera size={16} className="text-white" />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <Label className="text-base">Profile Photo</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload a professional photo. JPEG, PNG or WebP (max 5MB).
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Note: Photo will be uploaded after your registration is approved by admin.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>About / Bio <span className="text-destructive">*</span></Label>
                  <Textarea name="bio" value={formData.bio} onChange={handleChange} rows={3} placeholder="Brief introduction about yourself and your services" required />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input name="website" value={formData.website} onChange={handleChange} placeholder="https://" />
                </div>
                <div>
                  <Label>LinkedIn Profile</Label>
                  <Input name="linkedIn" value={formData.linkedIn} onChange={handleChange} placeholder="https://linkedin.com/in/" />
                </div>
              </div>
            </div>

            {/* Khatri Conclave 2026 Interest */}
            <div className="p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg border border-primary/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="interestedInConclave2026"
                  checked={formData.interestedInConclave2026}
                  onChange={(e) => setFormData(prev => ({ ...prev, interestedInConclave2026: e.target.checked }))}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-semibold text-gray-900">
                    I am interested in attending Khatri Conclave 2026
                  </span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Express your interest to attend the upcoming Khatri Conclave 2026. We will notify you with event details.
                  </p>
                </div>
              </label>
            </div>

            {/* Additional Info / Feedback */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Additional Information / Feedback
                <span className="text-sm font-normal text-gray-500 ml-2">अतिरिक्त जानकारी / सुझाव</span>
              </h3>
              <div>
                <Label>Any additional information or feedback / कोई अतिरिक्त जानकारी या सुझाव</Label>
                <Textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Share any additional information, feedback, or suggestions here... / अतिरिक्त जानकारी, सुझाव या प्रतिक्रिया यहाँ लिखें..."
                  data-testid="prof-additional-info"
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/professionals')}
                className="sm:w-auto"
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  'Submit Registration'
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ProfessionalRegistrationPage;
